import { Link } from "react-router-dom";
import { usePublicBlogPosts, type PublicBlogPost } from "@/hooks/useBlogPosts";
import { selectLatestPosts } from "@/lib/homeShowcase";
import { Photo } from "@/components/shared/Photo";
import { cn } from "@/lib/utils";

export type LatestPostItem = Pick<
  PublicBlogPost,
  "id" | "slug" | "title" | "excerpt" | "cover_image_url" | "blog_categories"
>;

export interface LatestPostsViewProps {
  /** Já cortados pelo container. Vazio = seção não renderiza. */
  posts: LatestPostItem[];
  className?: string;
}

/** "Do blog" na home: até 3 artigos recentes. Só apresentação; some se vazio. */
export function LatestPostsView({ posts, className }: LatestPostsViewProps) {
  if (posts.length === 0) return null;

  return (
    <section className={cn("mx-auto max-w-[1180px] px-8 pt-[70px] max-[900px]:px-5", className)}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="text-[31px] font-bold tracking-[-0.03em]">Do blog</h2>
        <Link to="/blog" className="text-sm font-bold text-primary hover:text-primary-deep">
          Todos os artigos →
        </Link>
      </div>
      <div className="mt-7 grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-[18px]">
        {posts.map((post) => (
          <Link
            key={post.id}
            to={`/blog/${post.slug}`}
            className="block overflow-hidden rounded-[18px] border border-border bg-card shadow-card transition-[border-color,transform] duration-150 hover:-translate-y-0.5 hover:border-primary"
          >
            <div className="aspect-[16/9] overflow-hidden border-b border-border">
              {post.cover_image_url ? (
                <img
                  src={post.cover_image_url}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              ) : (
                <Photo id="mesa-trabalho" decorative sizes="(max-width: 900px) 100vw, 380px" />
              )}
            </div>
            <div className="p-5">
              {post.blog_categories && (
                <div className="text-[11.5px] font-bold text-primary">
                  {post.blog_categories.name}
                </div>
              )}
              <h3 className="mt-1.5 text-[17px] font-bold leading-[1.35] tracking-[-0.02em]">
                {post.title}
              </h3>
              {post.excerpt && (
                <p className="mt-2 line-clamp-2 text-[13.5px] leading-[1.6] text-muted-foreground">
                  {post.excerpt}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/** Container do site: posts publicados → 3 mais recentes. */
const LatestPosts = () => {
  const { data } = usePublicBlogPosts();
  return <LatestPostsView posts={selectLatestPosts(data)} />;
};

export default LatestPosts;
